import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarClock, Clock, Globe, MapPin, Mail, Phone, RotateCcw, X, Check, UserX, Plus, StickyNote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { EmptyState } from "./EmptyState";
import { StatusBadge } from "./StatusBadge";
import { Button } from "./ui/button";
import { RescheduleDialog } from "./RescheduleDialog";
import { AddAppointmentDialog } from "./AddAppointmentDialog";
import { dateLocale } from "../lib/meta";

export function PsychologistAgenda() {
  const { t, lang } = useLang();
  const [appts, setAppts] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [resc, setResc] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [manualResc, setManualResc] = useState(null);

  const load = () => api.get("/appointments/mine").then((r) => setAppts(r.data)).catch(() => setAppts([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm(t("booking.confirm_cancel"))) return;
    try { await api.patch(`/appointments/${id}/cancel`); toast.success(t("booking.cancelled_ok")); load(); }
    catch { toast.error(t("booking.cancel_err")); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}/status`, { status }); toast.success(t("booking.status_updated")); load(); }
    catch { toast.error(t("booking.update_err")); }
  };

  if (appts === null) return <div className="py-10 text-center text-muted-foreground">{t("common.loading")}</div>;
  const now = new Date();
  const upcoming = appts.filter((a) => new Date(a.start_utc) >= now && a.status === "confirmed");
  const past = appts.filter((a) => new Date(a.start_utc) < now || ["cancelled", "completed", "no_show"].includes(a.status));
  const list = tab === "upcoming" ? upcoming : past;
  const fmt = (a) => new Date(a.start_utc).toLocaleString(dateLocale(lang), { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: a.psych_timezone });
  const label = (s) => t(`booking.st_${s}`);

  return (
    <div data-testid="psych-agenda">
      <p className="mb-3 text-xs text-muted-foreground">{t("booking.reminders_note")}</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-max gap-1 rounded-full border border-border bg-white p-1">
          {[["upcoming", t("booking.upcoming")], ["past", t("booking.past")]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`psy-appts-tab-${v}`} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === v ? "bg-slateink text-white" : "text-slateink/70"}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="psy-add-appt-btn"><Plus className="mr-1.5 h-4 w-4" />{t("booking.add_appt")}</Button>
      </div>
      {list.length === 0 ? (
        <div className="mt-6"><EmptyState icon={CalendarClock} title={t("booking.no_appts_psy")} testId="psy-appts-empty" /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {list.map((a) => (
            <div key={a.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`psy-appt-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-display font-semibold text-slateink">{a.client_name}{a.type_label && <span className="text-xs font-normal text-muted-foreground"> · {a.type_label}</span>}{a.source === "professional_manual" && <span className="ml-2 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground" data-testid={`psy-appt-manual-${a.id}`}>{t("booking.manual_tag")}</span>}</p>
                <StatusBadge status={a.status === "confirmed" ? "verified" : "pending"} label={label(a.status)} />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{fmt(a)}</span>
                <span className="inline-flex items-center gap-1"><Globe className="h-4 w-4" />{a.psych_timezone}</span>
                {a.modality && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{t(`common.${a.modality === "in_person" ? "in_person" : a.modality}`)}</span>}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {a.client_email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{a.client_email}</span>}
                {a.client_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{a.client_phone}</span>}
              </div>
              {a.private_note && <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-muted/60 px-2.5 py-1.5 text-xs text-slateink/70" data-testid={`psy-appt-note-${a.id}`}><StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" />{a.private_note}</p>}
              {tab === "upcoming" && a.status === "confirmed" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => a.source === "professional_manual" ? setManualResc(a) : setResc(a)} data-testid={`psy-resc-${a.id}`}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />{t("booking.reschedule")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(a.id, "completed")} data-testid={`psy-complete-${a.id}`}><Check className="mr-1.5 h-3.5 w-3.5" />{t("booking.mark_completed")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(a.id, "no_show")} data-testid={`psy-noshow-${a.id}`}><UserX className="mr-1.5 h-3.5 w-3.5" />{t("booking.mark_no_show")}</Button>
                  <Button size="sm" variant="outline" onClick={() => cancel(a.id)} data-testid={`psy-cancel-${a.id}`}><X className="mr-1.5 h-3.5 w-3.5" />{t("common.cancel")}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <RescheduleDialog open={!!resc} onOpenChange={(o) => !o && setResc(null)} appt={resc} viewerTz={resc?.psych_timezone} onDone={load} />
      <AddAppointmentDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
      <AddAppointmentDialog open={!!manualResc} onOpenChange={(o) => !o && setManualResc(null)} editAppt={manualResc} onDone={load} />
    </div>
  );
}
